import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'

interface EntityModalProps {
  open: boolean
  onClose: () => void
  onSubmit?: () => void
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  submitLabel?: string
  isSubmitting?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
  actions?: React.ReactNode
  submitDisabled?: boolean
  headerExtra?: React.ReactNode
  submitProps?: any
  hideClose?: boolean
}

export function EntityModal({
  open,
  onClose,
  onSubmit,
  title,
  icon,
  children,
  submitLabel = 'Save',
  isSubmitting = false,
  maxWidth = 'xs',
  fullWidth = true,
  actions,
  submitDisabled = false,
  headerExtra,
  submitProps,
  hideClose = false,
}: EntityModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        },
      }}
    >
      <DialogTitle sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: themeColors.background,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: themeColors.text.primary,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
            {title}
          </Typography>
          {headerExtra}
        </Box>
        {!hideClose && (
          <IconButton onClick={onClose} size="small" sx={{ color: '#9CA3AF' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        {children}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
        {actions ? (
          actions
        ) : (
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                color: '#374151',
                borderColor: '#E5E7EB',
                px: 3,
                '&:hover': {
                  borderColor: themeColors.primary,
                  bgcolor: themeColors.surfaceHover,
                },
              }}
            >
              Cancel
            </Button>
            {onSubmit && (
              <Button
                onClick={onSubmit}
                variant="contained"
                disabled={isSubmitting || submitDisabled}
                {...submitProps}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  background: themeColors.primary,
                  boxShadow: '0 4px 12px rgba(30, 58, 95, 0.2)',
                  '&:hover': {
                    background: themeColors.primaryDark,
                  },
                  ...(submitProps?.sx || {}),
                }}
              >
                {isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : submitLabel}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
